import {
  setBrandAccountOverviewInfo,
  setBrandBackendRequirementsData,
  setBrandConfigurationPIEChartsData,
  setBrandConfigurationReportList,
  setBrandOverviewAdvisorSettingsData,
  setBrandOverviewFetcherSettingsData,
  setBrandOverviewInfoData,
  setBrandOverviewLastRecordsData,
  setBrandOverviewLastRunData,
  setBrandOverviewUnifierSettingsData,
  setBrandPropertiesData,
  setFetcherConfigData,
  setIsSuccess,
  setObjectSectionData,
  setObjectSectionMetaData,
  setOverviewMetaData,
  setOverviewPerformanceData,
  setProcessFailure,
  setRawTableData,
  setResWorkflowName,
} from "../../../features/brandConfigurationReportSlice";
import { setDisplayErrorSnackBar } from "../../../features/userSlice";
import { brandConfigReportService } from "../../../services/api/brand-config-report.endpoints";
import store from "../../../store";

async function getBrandConfigurationReport(oauthTokenHeader: string) {
  try {
    const response =
      await brandConfigReportService.getBrandConfigurationReport(
        oauthTokenHeader,
      );
    if (!response || !response.status) {
      throw new Error("Failed to access brand configuration report.");
    } else if (response.status === 200) {
      const brandConfigReports = response?.data?.data ?? [];
      store.dispatch(setBrandConfigurationReportList(brandConfigReports)); // Set the fetched data
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message:
          e?.parsedText || "Failed to access brand configuration report.",
        type: "error",
      }),
    );
  }
}

async function getBrandConfigPIECharts(
  oauthTokenHeader: string,
  website_shared_uid: string,
) {
  try {
    const response = await brandConfigReportService.getBrandConfigPIECharts(
      oauthTokenHeader,
      website_shared_uid,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access brand configuration PIE charts.");
    } else if (response.status === 200) {
      const pieCharts = response?.data?.data?.query_output ?? [];
      store.dispatch(setBrandConfigurationPIEChartsData(pieCharts)); // Set the fetched data
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message:
          e?.parsedText || "Failed to access brand configuration PIE charts.",
        type: "error",
      }),
    );
  }
}

async function getBrandBackendRequirements(
  oauthTokenHeader: string,
  backend_shared_uid: string,
) {
  try {
    const response = await brandConfigReportService.getBrandBackendRequirements(
      oauthTokenHeader,
      backend_shared_uid,
    );
    if (!response || !response.status) {
      throw new Error(
        "Failed to access brand configuration backend requirements.",
      );
    } else if (response.status === 200) {
      const backendRequirements = response?.data?.data ?? [];
      store.dispatch(setBrandBackendRequirementsData(backendRequirements)); // Set the fetched data
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message:
          e?.parsedText ||
          "Failed to access brand configuration backend requirements.",
        type: "error",
      }),
    );
  }
}

async function getBrandProperties(
  oauthTokenHeader: string,
  brand_shared_uid: string,
) {
  try {
    const response = await brandConfigReportService.getBrandProperties(
      oauthTokenHeader,
      brand_shared_uid,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access brand properties.");
    } else if (response.status === 200) {
      const brandProperties = response?.data?.data ?? [];
      store.dispatch(setBrandPropertiesData(brandProperties)); // Set the fetched data
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Failed to access brand properties.",
        type: "error",
      }),
    );
  }
}

async function updateBrandProperties(
  oauthTokenHeader: string,
  data: any,
  brand_shared_uid: any,
) {
  try {
    const response = await brandConfigReportService.editBrandProperties(
      oauthTokenHeader,
      data,
      brand_shared_uid,
    );

    if (!response) {
      throw new Error("Failed to update brand properties.");
    }

    // Handle successful response codes
    if (response.status === 200) {
      //return response?.data;
      return response;
    }

    if (response.status === 207) {
      // Extract error message from response if available
      const errorMessage =
        "Failed to update brand properties. Please try again.";
      throw new Error(errorMessage);
    }

    // Handle client errors (400)
    if (response.status === 400) {
      throw new Error(
        response?.data?.reason ||
          "Something went wrong; please try again later...",
      );
    }

    // Handle server errors (502)
    if (response.status === 502) {
      throw new Error(
        "Server is temporarily unavailable. Please try again later.",
      );
    }

    // Handle other error status codes
    throw new Error(
      response?.data?.message || "Failed to update brand properties.",
    );
  } catch (e: any) {
    // Re-throw the error to be handled by the calling function
    throw e;
  }
}

async function postBrandConfigurationProcess(
  oauthTokenHeader: string,
  data: any,
) {
  try {
    const response =
      await brandConfigReportService.postBrandConfigurationReport(
        oauthTokenHeader,
        data,
      );
    if (!response || !response.status) {
      throw new Error("Failed to process brand integration.");
    } else if (response.status === 200) {
      return response;
    } else if (response.status === 400) {
      throw new Error("Bad request - invalid brand configuration data.");
    } else if (response.status === 502) {
      throw new Error("Bad gateway - service temporarily unavailable.");
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message:
          e?.parsedText || e?.message || "Failed to process brand integration.",
        type: "error",
      }),
    );
  }
}

//--- EP's Of Brand Configuration Overview Screen ---//
async function getBrandOverviewInfo(
  oauthTokenHeader: string,
  brand_id: string | any,
) {
  try {
    const response = await brandConfigReportService.getBrandOverviewInfo(
      oauthTokenHeader,
      brand_id,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access brand information.");
    } else if (response.status === 200) {
      const brandOverviewInfo = response?.data?.data ?? {};
      store.dispatch(setBrandOverviewInfoData(brandOverviewInfo)); // Set the fetched data
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Failed to access brand information.",
        type: "error",
      }),
    );
  }
}

async function getBrandOverviewLastRecords(
  oauthTokenHeader: string,
  brand_id: string | any,
  report: string | any,
) {
  try {
    const response = await brandConfigReportService.getBrandOverviewLastRecords(
      oauthTokenHeader,
      brand_id,
      report,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access brand last records.");
    } else if (response.status === 200) {
      const brandOverviewLastRecords = response?.data?.data?.records ?? [];
      store.dispatch(
        setBrandOverviewLastRecordsData({
          reportType: report,
          data: brandOverviewLastRecords,
        }),
      ); // Set the fetched data
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Failed to access brand last records.",
        type: "error",
      }),
    );
  }
}

async function getBrandOverviewLastRuns(
  oauthTokenHeader: string | any,
  brand_id: string | any,
) {
  try {
    const response = await brandConfigReportService.getBrandOverviewLastRuns(
      oauthTokenHeader,
      brand_id,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access brand last run information.");
    } else if (response.status === 200) {
      const brandOverviewLastRun = response?.data?.data ?? [];
      store.dispatch(setBrandOverviewLastRunData(brandOverviewLastRun)); // Set the fetched data
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message:
          e?.parsedText || "Failed to access brand last run information.",
        type: "error",
      }),
    );
  }
}

async function getBrandOverviewAdvisorSettings(
  oauthTokenHeader: string,
  brand_id: string | any,
  report: string | any,
) {
  try {
    const response =
      await brandConfigReportService.getBrandOverviewAdvisorSettings(
        oauthTokenHeader,
        brand_id,
        report,
      );
    if (!response || !response.status) {
      throw new Error("Failed to access brand advisor settings.");
    } else if (response.status === 200) {
      const brandOverviewAdvisorSettings = response?.data?.data ?? [];
      store.dispatch(
        setBrandOverviewAdvisorSettingsData({
          reportType: report,
          data: brandOverviewAdvisorSettings,
        }),
      ); // Set the fetched data
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Failed to access brand advisor settings.",
        type: "error",
      }),
    );
  }
}

async function getBrandOverviewUnifierSettings(
  oauthTokenHeader: string,
  brand_id: string | any,
  report: string | any,
) {
  try {
    const response =
      await brandConfigReportService.getBrandOverviewUnifierSettings(
        oauthTokenHeader,
        brand_id,
        report,
      );
    if (!response || !response.status) {
      throw new Error("Failed to access brand unifier settings.");
    } else if (response.status === 200) {
      const brandOverviewUnifierSettings = response?.data?.data ?? [];
      //Store under the specific reportType key
      store.dispatch(
        setBrandOverviewUnifierSettingsData({
          reportType: report,
          data: brandOverviewUnifierSettings,
        }),
      );
      return brandOverviewUnifierSettings; // Return the fetched data for potential further use
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Failed to access brand unifier settings.",
        type: "error",
      }),
    );
  }
}

async function getBrandOverviewFetcherSettings(
  oauthTokenHeader: string,
  reqBody: any,
  report: string | any,
) {
  try {
    const response =
      await brandConfigReportService.getBrandOverviewFetcherSettings(
        oauthTokenHeader,
        reqBody,
      );
    if (!response || !response.status) {
      throw new Error("Failed to access brand fetcher settings.");
    } else if (response.status === 200) {
      const brandOverviewFetcherSettings = response?.data?.data ?? [];

      store.dispatch(
        setBrandOverviewFetcherSettingsData({
          reportType: report,
          data: brandOverviewFetcherSettings,
        }),
      );

      //return { status: 200, data: brandOverviewFetcherSettings }; // Return the fetched data for potential further use
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Failed to access brand fetcher settings.",
        type: "error",
      }),
    );
  }
}
async function brandsRunProcess(oauthTokenHeader: string, data: any) {
  try {
    const response = await brandConfigReportService.brandsRunProcess(
      oauthTokenHeader,
      data,
    );
    if (!response.status || response.status !== 200) {
      store.dispatch(setIsSuccess(false));
      store.dispatch(
        setProcessFailure({
          isFailed: true,
          message: "Something went wrong while running brand process",
        }),
      );
    } else {
      store.dispatch(setProcessFailure({ isFailed: false, message: "" }));
      store.dispatch(setIsSuccess(true));
      const workflowName = response?.data?.data?.workflow_name;
      if (workflowName) {
        store.dispatch(setResWorkflowName(workflowName));
      } else {
        store.dispatch(
          setDisplayErrorSnackBar({
            message: "Workflow name not found in the response",
            type: "error",
          }),
        );
      }
    }
  } catch (e: any) {
    store.dispatch(
      setProcessFailure({
        isFailed: true,
        message: e?.parsedText || "Unknown error occurred",
      }),
    );
  }
}

async function getOverviewPerformance(
  oauthTokenHeader: string | any,
  report: string | any,
  queryId: string | any,
  website_shared_uid: string | any,
  type: string | any,
  brand_id: string | any,
  start_date: string | any,
  end_date: string | any,
  limit: string | any,
) {
  try {
    const response = await brandConfigReportService.getOverviewPerformance(
      oauthTokenHeader,
      queryId,
      website_shared_uid,
      type,
      brand_id,
      start_date,
      end_date,
      limit,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access overview performance.");
    } else if (response.status === 200) {
      const overViewData = response?.data?.data?.query_output ?? [];
      const overviewMetadata = response?.data?.data?.output_metadata ?? {};

      // store.dispatch(setOverviewPerformanceData(overViewData)); // Set the fetched data
      store.dispatch(
        setOverviewPerformanceData({
          reportType: report,
          data: overViewData,
        }),
      ); // Set the fetched data

      store.dispatch(
        setOverviewMetaData({ reportType: report, data: overviewMetadata }),
      ); // set the metadata

      return response.status;
    }
  } catch (e: any) {
    const status = e?.status || 500;
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
    return status;
  }
}

async function getObjectsData(
  oauthTokenHeader: string,
  queryId: string | any,
  website_shared_uid: string | any,
  type: string | any,
  brand_id: string | any,
) {
  try {
    const response = await brandConfigReportService.getObjectsData(
      oauthTokenHeader,
      queryId,
      website_shared_uid,
      type,
      brand_id,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access objects data.");
    } else if (response.status === 200) {
      const objectsData = response?.data?.data?.query_output ?? [];
      const objectsMetadata = response?.data?.data?.output_metadata ?? {};

      store.dispatch(setObjectSectionData(objectsData)); // Set the fetched data
      store.dispatch(setObjectSectionMetaData(objectsMetadata)); // set the metadata
    }
  } catch (e: any) {
    const status = e?.status || 500;
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
    return status;
  }
}

async function getBrandAccountOverviewInfo(
  oauthTokenHeader: string,
  queryId: string | any,
  website_shared_uid: string | any,
  type: string | any,
  brand_id: string | any,
) {
  try {
    const response = await brandConfigReportService.getBrandAccountOverviewInfo(
      oauthTokenHeader,
      queryId,
      website_shared_uid,
      type,
      brand_id,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access brand account overview data.");
    } else if (response.status === 200) {
      const brandAccountOverviewInfo = response?.data?.data?.query_output ?? [];
      store.dispatch(setBrandAccountOverviewInfo(brandAccountOverviewInfo)); // Set the fetched data
    }
  } catch (e: any) {
    const status = e?.status || 500;
    store.dispatch(
      setDisplayErrorSnackBar({
        message:
          e?.parsedText || "Failed to access brand account overview data.",
        type: "error",
      }),
    );
    return status;
  }
}

async function getAllConfigForFetcher(
  oauthTokenHeader: string,
  biz_layer_query_id: any,
  website_shared_uid: any,
) {
  try {
    const response = await brandConfigReportService.getAllConfigForFetcher(
      oauthTokenHeader,
      biz_layer_query_id,
      website_shared_uid,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access fetcher config.");
    } else if (response.status === 200) {
      const fetcherConfig = response?.data?.data ?? [];
      store.dispatch(setFetcherConfigData(fetcherConfig));
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Failed to access fetcher config.",
        type: "error",
      }),
    );
  }
}

async function getRawTableData(
  oauthTokenHeader: string,
  table_id: string | any,
  start_date: string | any,
  end_date: string | any,
  type: string | any,
  fieldMappingId?: string | any,
) {
  try {
    const response = await brandConfigReportService.getRawTableData(
      oauthTokenHeader,
      table_id,
      start_date,
      end_date,
      type,
      fieldMappingId
    );
    if (!response || !response.status) {
      throw new Error("Failed to access raw table data.");
    } else if (response.status === 200) {
      const rawTableData = response?.data?.data ?? [];

      store.dispatch(setRawTableData(rawTableData)); // Set the fetched data
    }
  } catch (e: any) {
    const status = e?.status || 500;
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
    return status;
  }
}

export const BrandConfigReportEndPointLogic = {
  getBrandConfigurationReport,
  getBrandConfigPIECharts,
  getBrandBackendRequirements,
  getBrandProperties,
  updateBrandProperties,
  postBrandConfigurationProcess,
  getBrandOverviewInfo,
  getBrandOverviewLastRecords,
  getBrandOverviewLastRuns,
  getBrandOverviewAdvisorSettings,
  getBrandOverviewUnifierSettings,
  getBrandOverviewFetcherSettings,
  brandsRunProcess,
  getAllConfigForFetcher,
  getOverviewPerformance,
  getObjectsData,
  getBrandAccountOverviewInfo,
  getRawTableData
};
